<template>
  <div class="row d-flex">
    <div class="col-md-12 mr-auto ml-auto modal-overflow">
      <job-wizard ref="wizard">
        <wizard-tab id="select-workflow" :before-change="() => selectTemplateOrWorkflow()"  @active-toggled="setNextButtonActiveState">
          <template slot="label">
            <i class="nc-icon nc-ruler-pencil"></i>
            Select Template OR Workflow
          </template>
          <select-template-or-workflow ref="selectTemplateOrWorkflow" :data="{projectDepartments}"></select-template-or-workflow>
        </wizard-tab>

        <wizard-tab :before-change="() => checkClientAndPoRequiredAndSave()">
          <template slot="label">
            <i class="nc-icon nc-bullet-list-67"></i>
            Job Details
          </template>
          <add-job ref="addJob"></add-job>
        </wizard-tab>
      </job-wizard>
    </div>
  </div>
</template>

<script>
import {notifySuccess, notifyError, sleepOneSecond} from 'src/utilities/utils'
import {jobCheckClientAndPoRequiredAndSave, jobValidateContactEmails} from 'src/utilities/jobHelper'
import Constants from 'src/utilities/constants'
import SelectTemplateOrWorkflow from './SelectTemplateOrWorkflow'
import AddJob from './AddJob'
import JobWizard from 'src/components/UIComponents/Wizard/JobWizard'
import WizardTab from 'src/components/UIComponents/Wizard/WizardTab'
import {OK, CREATED} from 'http-status-codes'
import {ptracPost} from '@/utilities/ptracApi'

const CREATE = 'create'

export default {
  props: ['data'],
  components: {
    AddJob,
    JobWizard,
    SelectTemplateOrWorkflow,
    WizardTab
  },
  data() {
    return {
      wizardModel: {},
      isProcessing: false,
      purchaseOrderNumber: null
    }
  },
  computed: {
    projectDepartments() {
      return this.$store.get('projects/edit@departments')
    }
  },
  methods: {
    setNextButtonActiveState(payload) {
      if (payload.active) {
        if (payload.id === 'select-workflow') {
          this.$store.commit('wizard/nextDisabled', true)
          if (this.$store.get('jobs/create@processDefinitionKey')) {
            this.$store.commit('wizard/nextDisabled', false)
          }
        }
      }
    },
    selectTemplateOrWorkflow() {
      if (this.$store.get('jobs/create@processDefinitionKey')) {
        this.$refs.wizard.activeTabIndex++
      }
      return true
    },
    async loadElasticsearchResults(jobId) {
      // try up to 10 times for an Elasticsearch search result, with 1 second search interval
      for (let i = 0; i < 10; i++) {
        if (await this.loadElasticsearchTasks(jobId)) {
          return true
        }
        await sleepOneSecond()
      }
      return false
    },
    async loadPtracJobTaskResults(jobId) {
      // try up to 5 times for PTRAC to return tasks for the newly created job, with 1 second search interval
      for (let i = 0; i < 5; i++) {
        const ptracTasks = await this.loadPtracTasks(jobId)
        if (ptracTasks && ptracTasks.length > 0) {
          return ptracTasks
        }
        await sleepOneSecond()
      }
      return null
    },
    async checkClientAndPoRequiredAndSave() {
      await jobCheckClientAndPoRequiredAndSave(this, CREATE)
    },
    async validateContactEmails() {
      await jobValidateContactEmails(this, CREATE)
    },
    async validateJobDetailsAndSubmit() {
      if (await this.$refs.addJob.validate() && !this.isProcessing) {
        this.isProcessing = true
        const job = this.$store.get('jobs/create')
        // hacky workaround - ckEditor having issues with null description in vuex
        job.description = this.$refs.addJob.localDescription

        const response = await ptracPost('jobs/create-job-and-initiate-process-instance', job)

        if (CREATED === response.status) {
          const createdJob = response.data
          if (createdJob.status === Constants.STATUSES.ERROR) {
            notifyError(this, 'Job created but with errors. Please inspect error log before using this Job')
          } else {
            notifySuccess(this, 'Job Created Successfully')
          }

          if (this.purchaseOrderNumber) {
            await this.addPoNumberToInvoices(createdJob.id)
          }

          await this.resetCreateJobState(createdJob.projectId)

          // route to task search
          if (this.$store.get('wizard/finishAndViewTasks')) {
            await this.redirectToTaskSearchResults(createdJob)
          } else if (this.$store.get('wizard/finishAndEditTasks')) {
            await this.closeWizardAndViewEditTasksModal(createdJob)
            this.$emit('close')
          } else {
            this.$emit('close')
          }

          this.isProcessing = false
          return
        }

        this.isProcessing = false
        setTimeout(() => notifyError(this, 'Failed to Create Job', response.errors), Constants.TIME.MILLIS.ONE_SECOND)
        await this.resetCreateJobState(job.projectId)
        setTimeout(() => this.$emit('close'), Constants.TIME.MILLIS.ONE_SECOND)
      }
    },
    async resetCreateJobState(projectId) {
      this.$refs.addJob.resetLocalState()
      await this.$store.dispatch('jobs/fetchByProjectId', projectId)
      this.$store.commit('jobs/resetCreate')
    },
    async closeWizardAndViewEditTasksModal(job) {
      if (await this.loadPtracJobTaskResults(job.id)) {
        this.$eventHub.$emit('show-modal-2', 'Bulk Edit Tasks', {jobId: job.id}, '95%')
      }
    },
    async redirectToTaskSearchResults(job) {
      // give monstache a moment to sync tasks from Mongo to Elasticsearch
      const data = {
        message: 'Loading tasks... please wait a moment',
        seconds: Constants.TIME.MILLIS.TEN_SECONDS // timeout setting
      }
      this.$eventHub.$emit('show-modal-1', 'Please Wait', data, '600px')

      if (await this.loadElasticsearchResults(job.id)) {
        this.$eventHub.$emit('closePleaseWait')
        this.goSeeTasks(job.id)
      } else {
        notifyError(this, 'Failed to load tasks. Please wait a moment and try again using the "See Tasks" button in the Job table.')
      }

      this.$store.commit('wizard/finishAndViewTasks', false)
    },
    async loadPtracTasks(jobId) {
      const response = await this.$store.dispatch('tasks/fetchTasksByJobId', jobId)

      if (OK === response.status && response.data.length > 0) {
        return response.data
      }
      return null
    },
    async loadElasticsearchTasks(jobId) {
      this.$store.set('tasks/search/query@status', [])
      this.$store.set('tasks/search/query@jobId', jobId)
      const response = await this.$store.dispatch('tasks/search/fetchSearchResults', 1)
      return response && response.hits && response.hits.hits && response.hits.hits.length > 0
    },
    goSeeTasks(jobId) {
      this.$store.commit('tasks/search/resetQuery')
      this.$store.commit('tasks/search/resetResults')
      this.$router.push({path: `/tasks/search?jobId=${jobId}`})
      this.$emit('close')
    },
    async jobClientOrPoChecksComplete(poNumber) {
      if (poNumber) {
        this.purchaseOrderNumber = poNumber
      }
      await this.validateContactEmails()
    },
    async addPoNumberToInvoices(jobId) {
      const tasks = await this.loadPtracJobTaskResults(jobId)

      if (!tasks || tasks.length === 0) {
        return
      }

      const invoiceTasks = tasks.filter(task => task.name.startsWith(Constants.TASKS.NAMES.INVOICE))

      for (const invoiceTask of invoiceTasks) {
        invoiceTask.fieldValues.poNumber = this.purchaseOrderNumber

        // don't use save2 - sometimes tasks are returned before they are fully setup and associated with their complimentary Camunda task
        // PATCH allows updating just the fields needed rather than the whole object.
        const response = await this.$store.dispatch('mergePatch2', {
          id: invoiceTask.id,
          endpoint: 'tasks',
          data: { fieldValues: invoiceTask.fieldValues}
        })

        if (OK === response.status) {
          notifySuccess(this, 'Purchase Order Number successfully added to Invoice Task.')
        } else {
          notifyError(this, 'Unable to update Invoice Task. Please input Purchase Order data manually.')
        }
      }

      this.purchaseOrderNumber = null
    }
  },
  mounted() {
    this.$store.commit('wizard/nextDisabled', true)
    this.$eventHub.$on('jobClientOrPoChecksComplete', this.jobClientOrPoChecksComplete)
    this.$eventHub.$on('doNotAddContactEmail', this.validateJobDetailsAndSubmit)
  },
  destroyed() {
    this.$eventHub.$off('jobClientOrPoChecksComplete')
    this.$eventHub.$off('doNotAddContactEmail')
    this.$store.commit('wizard/finishAndViewTasks', false)
    this.$store.commit('wizard/finishAndEditTasks', false)
  }
}
</script>

<style scoped>
  .vue-form-wizard .wizard-icon-circle.tab_shape {
    background-color: #9A9A9A !important;
    color: white;
  }
  .card {
    box-shadow: none;
  }
  .tab-content-min-height {
    min-height: 150px;
  }
</style>
